import {
  Button,
  CardActionArea,
  type CardActionAreaProps,
} from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';

import {
  StyledCard,
  StyledCardActions,
  StyledCardContent,
  StyledHeadingBodyContainer,
  StyledTitle,
} from './DocumentCard.styles';
import { proAccountThemeCoreUI } from '@orcha/micro-library-core-ui';
import Link from 'next/link';

type CardAction = {
  link?: string;
  isExternalLink?: boolean;
};

type CardOptions = {
  isClickable: boolean;
  hasOutline: boolean;
};

export type DocumentCardProps = {
  title?: string;
  cardAction?: CardAction;
  primaryButton?: { text?: string; link?: string; isExternalLink?: boolean };
  cardOptions?: CardOptions;
  queryParams?: string;
};

/**
 * DocumentCard component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {DocumentCardProps} props - The props for the DocumentCard component
 * @returns {ReactElement} The DocumentCard component
 */

export const DocumentCard = ({
  cardOptions,
  title,
  cardAction,
  primaryButton,
  queryParams,
}: DocumentCardProps) => {
  const isClickable = cardOptions?.isClickable ?? false;
  const cardVariant = cardOptions?.hasOutline ? 'outlined' : 'elevation';

  const cardContent = (
    <StyledHeadingBodyContainer>
      <StyledCardContent
        cardWithButtonPadding={proAccountThemeCoreUI.spacing(1)}
      >
        {title ? <StyledTitle variant="h5">{title}</StyledTitle> : null}
      </StyledCardContent>
    </StyledHeadingBodyContainer>
  );

  const cardActionLinkProps = (
    cardAction?.link
      ? {
          LinkComponent: cardAction?.isExternalLink ? 'a' : Link,
          href: `${cardAction.link}?${queryParams}`,
          target: cardAction.isExternalLink ? '_blank' : undefined,
        }
      : {}
  ) as CardActionAreaProps;

  return (
    <StyledCard variant={cardVariant} elevation={0}>
      {isClickable ? (
        <CardActionArea
          {...cardActionLinkProps}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
        >
          {cardContent}
        </CardActionArea>
      ) : (
        cardContent
      )}

      {primaryButton && (
        <StyledCardActions
          cardWithButtonPadding={proAccountThemeCoreUI.spacing(1.5)}
        >
          <Button
            data-testid="document-card-primary-button"
            {...(primaryButton?.link
              ? {
                  LinkComponent: cardAction?.isExternalLink ? 'a' : Link,
                  href: `${primaryButton.link}?${queryParams}`,
                  target: primaryButton.isExternalLink ? '_blank' : undefined,
                }
              : {})}
            variant="contained"
            color="primary"
            endIcon={primaryButton.isExternalLink && <OpenInNew />}
            sx={{ minWidth: { xs: '115px', sm: 'initial', md: 'initial' } }}
          >
            {primaryButton.text}
          </Button>
        </StyledCardActions>
      )}
    </StyledCard>
  );
};
